/* eslint-disable react/button-has-type */
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { styled, alpha } from '@mui/material/styles';
import {
  Box,
  Link,
  Drawer,
  Typography,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BarChartIcon from '@mui/icons-material/BarChart';
// hooks
import useResponsive from '../../../hooks/useResponsive';
import { StyledNavItem } from '../../../components/nav-section/styles';
// components
import { ReactComponent as ManageIcon } from '../../../media/obm.svg';
import { ReactComponent as Ops } from '../../../media/opsss.svg';
import { ReactComponent as MastersIcon } from '../../../media/pd.svg';
import { ReactComponent as ReportsIcon } from '../../../media/prrd.svg';
import Scrollbar from '../../../components/scrollbar';
import NavSection from '../../../components/nav-section';
import './nav.css';
//
import OBProduction from './OBProduction';
import PRProduction from './PRProduction';
import configMaster from './configMaster';
import productionMaster from './productionMaster';
import logo from '../../../media/ob.png';
import Reports from './Reports';
import ProductionReports from './ProductionReports';
// ----------------------------------------------------------------------

const NAV_WIDTH = 280;

const StyledAccount = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  backgroundColor: alpha(theme.palette.grey[500], 0.12),
}));

// Custom styled components for better scrolling
const ScrollableContainer = styled(Box)({
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

// Custom styled Accordion for better scrolling
const StyledAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: 'inherit',
  border: 'none',
  boxShadow: 'none',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: 0,
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  minHeight: '48px !important',
  '& .MuiAccordionSummary-content': {
    margin: '4px 0 !important',
    alignItems: 'center',
    overflow: 'hidden',
  },
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(0.5, 0),
  borderTop: 'none',
}));

const NavContent = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0, // Important for flexbox scrolling
});

const MenuItemText = styled(Typography)(({ theme }) => ({
  color: 'white',
  fontSize: '0.9rem',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  flex: 1,
  minWidth: 0,
}));

// ----------------------------------------------------------------------

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};

export default function Nav({ openNav, onCloseNav }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isDesktop = useResponsive('up', 'lg');

  const [hasDashboardAccess, setHasDashboardAccess] = useState(false);
  const [hasConfigurationAccess, setHasConfigurationAccess] = useState(false);
  const [hasProductionAccess, setHasProductionAccess] = useState(false);
  const [hasOBProductionAccess, setHasOBProductionAccess] = useState(false);
  const [hasPRProductionAccess, setHasPRProductionAccess] = useState(false);

  useEffect(() => {
    // Check session storage and update state
    setHasDashboardAccess(sessionStorage.getItem('Dashboard') === 'Y');
    setHasConfigurationAccess(
      sessionStorage.getItem('Masters_Employee') === 'Y' ||
        sessionStorage.getItem('Masters_Buyer') === 'Y' ||
        sessionStorage.getItem('Masters_Company') === 'Y' ||
        sessionStorage.getItem('Masters_FactoryUnit') === 'Y' ||
        sessionStorage.getItem('Masters_Parameters') === 'Y' ||
        sessionStorage.getItem('Masters_Department') === 'Y' ||
        sessionStorage.getItem('Masters_Designation') === 'Y' ||
        sessionStorage.getItem('Masters_UserPreviledges') === 'Y' ||
        sessionStorage.getItem('Masters_AccessSettings') === 'Y' ||
        sessionStorage.getItem('Masters_ControlCenter') === 'Y'
    );
    setHasProductionAccess(
      sessionStorage.getItem('Masters_GarmentType') === 'Y' ||
        sessionStorage.getItem('Masters_Garments') === 'Y' ||
        sessionStorage.getItem('Masters_GarmentCategory') === 'Y' ||
        sessionStorage.getItem('Masters_Machine') === 'Y' ||
        sessionStorage.getItem('Masters_OperationGroup') === 'Y' ||
        sessionStorage.getItem('Masters_Operation') === 'Y' ||
        sessionStorage.getItem('Masters_RateCard') === 'Y' ||
        sessionStorage.getItem('Masters_Batch_Line') === 'Y' ||
        sessionStorage.getItem('Masters_grade') === 'Y'
    );
    setHasOBProductionAccess(
      sessionStorage.getItem('Transaction_OperationBulletin') === 'Y' ||
        sessionStorage.getItem('Transaction_Time_and_Study') === 'Y' ||
        sessionStorage.getItem('Transaction_DailyProduction') === 'Y' ||
        sessionStorage.getItem('Transaction_OtherProduction') === 'Y' ||
        sessionStorage.getItem('Reports_HourlyProduction') === 'Y' ||
        sessionStorage.getItem('Reports_MonthlyProduction') === 'Y' ||
        sessionStorage.getItem('Reports_SkillMatrixDaily') === 'Y' ||
        sessionStorage.getItem('Reports_ObStatus') === 'Y' ||
        sessionStorage.getItem('Reports_ReportTracker') === 'Y' ||
        sessionStorage.getItem('Reports_DayWiseProductionCostReport') === 'Y'
    );
    setHasPRProductionAccess(
      sessionStorage.getItem('Transaction_StyleRateCard') === 'Y' ||
        sessionStorage.getItem('Transaction_SRCApproval') === 'Y' ||
        sessionStorage.getItem('Transaction_PRProduction') === 'Y' ||
        sessionStorage.getItem('Reports_ProductionReportCard') === 'Y' ||
        sessionStorage.getItem('Reports_ProductionHrsWise') === 'Y' ||
        sessionStorage.getItem('Reports_SkillMatrixPR') === 'Y'
    );
  }, [pathname, openNav]);

  const isSplUser = sessionStorage.getItem('SplUser') === 'Y';

  useEffect(() => {
    if (!openNav) {
      onCloseNav();
    }
  }, [pathname]);

  const Report =
    sessionStorage.getItem('Reports_DayWiseProductionCostReport') === 'N' &&
    sessionStorage.getItem('Reports_HourlyProduction') === 'N' &&
    sessionStorage.getItem('Reports_MonthlyProduction') === 'N' &&
    sessionStorage.getItem('Reports_SkillMatrixDaily') === 'N' &&
    sessionStorage.getItem('Reports_ObStatus') === 'N' &&
    sessionStorage.getItem('Reports_ReportTracker') === 'N' &&
    sessionStorage.getItem('Reports_ProcessTracker') === 'N' &&
    sessionStorage.getItem('Reports_factoryexpensereport') === 'N' &&
    sessionStorage.getItem('Reports_ProcessEmpMap') === 'N';

  const ProductionReport =
    sessionStorage.getItem('Reports_ProductionHrsWise') === 'N' &&
    sessionStorage.getItem('Reports_ProductionReportCard') === 'N' &&
    sessionStorage.getItem('Reports_SkillMatrixPR') === 'N';

  const renderContent = (
    <ScrollableContainer>
      {/* Logo Section - Fixed at top */}
      <Box sx={{ flexShrink: 0, mb: 2, mx: 2, mt: 2 }}>
        <Link underline="none">
          <StyledAccount>
            <button
              onClick={() => {
                console.log('Clicked on the logo');
                navigate('/dashboard/home');
              }}
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <img
                src={logo}
                style={{
                  objectFit: 'contain',
                  maxWidth: '60%',
                  borderRadius: '10px',
                }}
                alt="techveel logo"
              />
            </button>
          </StyledAccount>
        </Link>
      </Box>

      {/* Navigation Items - Scrollable */}
      <Scrollbar
        sx={{
          flex: 1,
          '& .simplebar-content': {
            height: 'auto',
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
          '& .simplebar-track.simplebar-vertical': {
            right: 4,
          },
        }}
      >
        <NavContent>
          {/* Production */}
          {(hasProductionAccess || isSplUser) && (
            <StyledAccordion disableGutters>
              <StyledAccordionSummary
                expandIcon={<ExpandMoreIcon style={{ color: 'white', fontSize: 20 }} />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', minWidth: 0 }}>
                  <IconButton
                    aria-label="Production"
                    sx={{
                      color: 'white',
                      padding: '4px',
                      '& svg': { width: 22, height: 22 },
                    }}
                  >
                    <MastersIcon />
                  </IconButton>
                  <MenuItemText>Production</MenuItemText>
                </Stack>
              </StyledAccordionSummary>

              <StyledAccordionDetails>
                <Box sx={{ pl: 3, pr: 1 }}>
                  <NavSection data={productionMaster} />
                </Box>
              </StyledAccordionDetails>
            </StyledAccordion>
          )}

          {/* Configuration */}
          {(hasConfigurationAccess || isSplUser) && (
            <StyledAccordion disableGutters>
              <StyledAccordionSummary
                expandIcon={<ExpandMoreIcon style={{ color: 'white', fontSize: 20 }} />}
                aria-controls="panel2a-content"
                id="panel2a-header"
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', minWidth: 0 }}>
                  <IconButton
                    aria-label="Configuration"
                    sx={{
                      color: 'white',
                      padding: '4px',
                      '& svg': { width: 24, height: 24 },
                    }}
                  >
                    <Ops />
                  </IconButton>
                  <MenuItemText>Configuration</MenuItemText>
                </Stack>
              </StyledAccordionSummary>

              <StyledAccordionDetails>
                <Box sx={{ pl: 3, pr: 1 }}>
                  <NavSection data={configMaster} />
                </Box>
              </StyledAccordionDetails>
            </StyledAccordion>
          )}

          {/* OB Production */}
          {(hasOBProductionAccess || isSplUser) && (
            <StyledAccordion disableGutters>
              <StyledAccordionSummary
                expandIcon={<ExpandMoreIcon style={{ color: 'white', fontSize: 20 }} />}
                aria-controls="panel3a-content"
                id="panel3a-header"
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', minWidth: 0 }}>
                  <IconButton
                    aria-label="OB Production"
                    sx={{
                      color: 'white',
                      padding: '4px',
                      '& svg': { width: 22, height: 22 },
                    }}
                  >
                    <ManageIcon />
                  </IconButton>
                  <MenuItemText>OB Production</MenuItemText>
                </Stack>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <Box sx={{ pl: 3, pr: 1 }}>
                  <NavSection data={OBProduction} />
                 </Box>
                 </StyledAccordionDetails>
                   </StyledAccordion>
          )}
                  {/* Nested Accordion for Reports */}
                  {!Report && (
                    <StyledAccordion disableGutters >
                      <StyledAccordionSummary
                        expandIcon={<ExpandMoreIcon style={{ color: 'white', fontSize: 20 }} />}
                        aria-controls="report-content"
                        id="report-header"
                      >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', minWidth: 0 }}>
                          <IconButton
                    aria-label="OB Production"
                    sx={{
                      color: 'white',
                      padding: '4px',
                      '& svg': { width: 22, height: 22 },
                    }}
                  >
                          <BarChartIcon/>
                          </IconButton>
                          <MenuItemText >Reports</MenuItemText>
                        </Stack>
                      </StyledAccordionSummary>
                      <StyledAccordionDetails>
                        <Box sx={{ pl: 3, pr: 1 }}>
                          <NavSection data={Reports} />
                        </Box>
                      </StyledAccordionDetails>
                    </StyledAccordion>
                  )}
               
              
          

          {/* PR Production */}
          {(hasPRProductionAccess || isSplUser) && (
            <StyledAccordion disableGutters>
              <StyledAccordionSummary
                expandIcon={<ExpandMoreIcon style={{ color: 'white', fontSize: 20 }} />}
                aria-controls="panel4a-content"
                id="panel4a-header"
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', minWidth: 0 }}>
                  <IconButton
                    aria-label="PR Production"
                    sx={{
                      color: 'white',
                      padding: '4px',
                      '& svg': { width: 22, height: 22 },
                    }}
                  >
                    <ReportsIcon />
                  </IconButton>
                  <MenuItemText>PR Production</MenuItemText>
                </Stack>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <Box sx={{ pl: 3, pr: 1 }}>
                  <NavSection data={PRProduction} />

                  {/* Nested Accordion for ProductionReports */}
                  {!ProductionReport && (
                    <StyledAccordion disableGutters sx={{ mt: 0.5, backgroundColor: 'rgba(255,255,255,0.05)' }}>
                      <StyledAccordionSummary
                        expandIcon={<ExpandMoreIcon style={{ color: 'white', fontSize: 18 }} />}
                        aria-controls="production-report-content"
                        id="production-report-header"
                      >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', minWidth: 0 }}>
                          <BarChartIcon style={{ color: 'white', width: 20, height: 20 }} />
                          <MenuItemText sx={{ fontSize: '0.85rem' }}>Reports</MenuItemText>
                        </Stack>
                      </StyledAccordionSummary>
                      <StyledAccordionDetails>
                        <Box sx={{ pl: 2, pr: 1 }}>
                          <NavSection data={ProductionReports} />
                        </Box>
                      </StyledAccordionDetails>
                    </StyledAccordion>
                  )}
                </Box>
              </StyledAccordionDetails>
            </StyledAccordion>
          )}

          {/* Spacer to push content up */}
          <Box sx={{ flexGrow: 1 }} />
        </NavContent>
      </Scrollbar>
    </ScrollableContainer>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: openNav ? NAV_WIDTH : 0 },
      }}
    >
      {isDesktop ? (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          variant="persistent"
          PaperProps={{
            sx: {
              width: NAV_WIDTH,
              bgcolor: '#5588A3',
              overflow: 'hidden',
            },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              width: NAV_WIDTH,
              bgcolor: '#5588A3',
              overflow: 'hidden',
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
